<template>
  <div class="pb-page wl">
    <PageHeader title="Hosted wall" :subtitle="'A public page of ' + (brand ? brand.name : 'your brand') + '\'s testimonials, with its own link. No website needed.'" wide-only>
      <span v-if="dirty" class="pb-muted">Unsaved changes</span>
      <button type="button" class="pb-button" :disabled="!dirty || saving" @click="save">{{ saving ? 'Saving…' : 'Save changes' }}</button>
    </PageHeader>

    <SkeletonList v-if="loading" :rows="3" label="Loading the wall…" />
    <div v-else-if="loadError" class="pb-alert is-error" role="alert"><span>{{ loadError }}</span><button type="button" class="pb-button sm outline" @click="load">Retry</button></div>

    <template v-else-if="data">
      <div v-if="error" class="pb-alert is-error" role="alert"><span>{{ error }}</span></div>
      <div class="wl-grid">
        <div class="wl-settings">
          <section class="pb-card" aria-labelledby="wl-status">
            <h2 id="wl-status" class="pb-section-title display-font wl-h">Status</h2>
            <div class="pb-segmented" role="radiogroup" aria-labelledby="wl-status">
              <button v-for="option in statuses" :key="option.key" type="button" role="radio" :aria-checked="status === option.key ? 'true' : 'false'"
                      :aria-pressed="status === option.key ? 'true' : 'false'" @click="status = option.key">{{ option.label }}</button>
            </div>
            <p class="pb-help">{{ statusHelp }}</p>
            <div class="wl-link">
              <SnippetBox :value="data.url" label="Wall link" @copied="copied" />
              <div class="pb-actions wl-link-actions">
                <a :href="data.url" target="_blank" rel="noopener" class="pb-button sm outline">Open the wall<span class="pb-sr-only"> (opens in a new tab)</span></a>
                <button type="button" class="pb-button sm ghost" :aria-expanded="showQr ? 'true' : 'false'" @click="showQr = !showQr">QR code</button>
              </div>
              <QrCode v-if="showQr" :value="data.url" :size="160" :filename="'trustclap-wall-' + (brand ? brand.slug : 'wall')" />
            </div>
          </section>

          <section class="pb-card" aria-labelledby="wl-text">
            <h2 id="wl-text" class="pb-section-title display-font wl-h">Words</h2>
            <div class="pb-field"><label class="pb-label" for="wl-headline">Headline</label>
              <input id="wl-headline" v-model="settings.headline" class="pb-input" maxlength="120"></div>
            <div class="pb-field"><label class="pb-label" for="wl-intro">Intro <span class="optional">(optional)</span></label>
              <textarea id="wl-intro" v-model="settings.intro" class="pb-textarea" rows="3" maxlength="600"></textarea></div>
            <p class="pb-note"><i class="mdi mdi-shield-check-outline pb-ico" aria-hidden="true"></i><span>Every wall says: "{{ data.disclosure }}" It can't be turned off.</span></p>
          </section>

          <section class="pb-card" aria-labelledby="wl-look">
            <h2 id="wl-look" class="pb-section-title display-font wl-h">Layout and order</h2>
            <LayoutPicker :value="settings.layout" :allowed="data.limits.layouts" @pick="pickLayout" />
            <div class="pb-field wl-order"><label class="pb-label" for="wl-order">Order</label>
              <select id="wl-order" v-model="settings.order" class="pb-select"><option value="newest">Newest first</option><option value="oldest">Oldest first</option>
                <option value="rating">Highest rating first</option><option value="manual">Manual order (Testimonials page)</option></select></div>
            <label class="pb-check"><input v-model="settings.show_rating" type="checkbox">Show star ratings on each testimonial</label>
            <label class="pb-check wl-gap"><input type="checkbox" :checked="settings.remove_branding" @change="toggleBranding($event)">Hide "Collected with TrustClap"
              <PlanChip v-if="!data.limits.remove_branding" :plan="availableOn('remove_branding')" /></label>
          </section>

          <section class="pb-card" aria-labelledby="wl-sel">
            <h2 id="wl-sel" class="pb-section-title display-font wl-h">Which testimonials</h2>
            <label class="pb-check"><input v-model="settings.selection.mode" type="radio" value="all">All published testimonials</label>
            <label class="pb-check wl-gap"><input v-model="settings.selection.mode" type="radio" value="picked">Only the ones I pick</label>
            <div v-if="settings.selection.mode === 'picked'" class="wl-pick">
              <label v-for="item in data.published" :key="item.id" class="pb-check wl-pick-item">
                <input type="checkbox" :checked="settings.selection.ids.indexOf(item.id) >= 0" @change="togglePick(item.id)">
                <span><span class="wl-pick-text">{{ item.text }}</span><span class="pb-muted">{{ item.name || item.byline }}</span></span>
              </label>
            </div>
          </section>
        </div>

        <section class="wl-preview" aria-labelledby="wl-prev">
          <h2 id="wl-prev" class="pb-label">Preview</h2>
          <div class="wl-frame">
            <div class="wl-bar" aria-hidden="true"><span></span><span></span><span></span><em>{{ data.url.replace(/^https?:\/\//, '') }}</em></div>
            <div class="wl-page">
              <WallBody :brand="data.brand" :settings="settings" :layout="settings.layout" :items="previewItems" :disclosure="data.disclosure"
                        :removal-url="data.removal_url" :attribution="previewAttribution" preview />
            </div>
          </div>
        </section>
      </div>
    </template>
    <SaveBar :dirty="dirty" :saving="saving" @save="save" />
    <UpgradeDialog v-model="upgradeOpen" :detail="upgrade" />
  </div>
</template>

<script>
import LayoutPicker from '../components/LayoutPicker.vue';
import PageHeader from '../components/PageHeader.vue';
import PlanChip from '../components/PlanChip.vue';
import SaveBar from '../components/SaveBar.vue';
import QrCode from '../components/QrCode.vue';
import SkeletonList from '../components/SkeletonList.vue';
import SnippetBox from '../components/SnippetBox.vue';
import UpgradeDialog from '../components/UpgradeDialog.vue';
import WallBody from '../components/WallBody.vue';
import workspace from '../mixins/workspace';
import { get, patch, message, planLimit } from '../api';
import { ask } from '../lib/confirm';
import { toast } from '../lib/toast';

export default {
  name: 'WallSettingsView',
  components: { LayoutPicker, PageHeader, PlanChip, QrCode, SaveBar, SkeletonList, SnippetBox, UpgradeDialog, WallBody },
  mixins: [workspace],
  data: () => ({ data: null, status: 'draft', settings: null, saved: '', loading: true, loadError: '', error: '', saving: false, showQr: false,
    upgrade: null, upgradeOpen: false,
    statuses: [{ key: 'draft', label: 'Draft' }, { key: 'live', label: 'Live' }, { key: 'paused', label: 'Paused' }] }),
  computed: {
    snapshot() { return JSON.stringify({ status: this.status, settings: this.settings }); },
    dirty() { return !!this.settings && this.snapshot !== this.saved; },
    statusHelp() {
      return { draft: 'Only you can see the preview. Visitors see "This page isn\'t available right now."',
        live: 'Anyone with the link can see it. It updates on its own when you publish or unpublish.',
        paused: 'Temporarily hidden. Visitors see "This page isn\'t available right now."' }[this.status];
    },
    previewItems() {
      let items = this.data.published.slice();
      const byId = id => items.find(item => item.id === id);
      if (this.settings.selection.mode === 'picked') {
        items = this.settings.selection.ids.map(byId).filter(Boolean);
      } else if (this.settings.order === 'oldest') items.reverse();
      else if (this.settings.order === 'rating') items.sort((a, b) => (b.rating || 0) - (a.rating || 0));
      else if (this.settings.order === 'manual') items = (this.data.manual_ids || []).map(byId).filter(Boolean);
      return items.slice(0, 60);
    },
    previewAttribution() {
      return { show: !(this.settings.remove_branding && this.data.limits.remove_branding), href: this.data.attribution.href };
    }
  },
  watch: { brandId() { this.load(); } },
  async mounted() {
    if (!(await this.requireWorkspace())) return;
    this.load();
  },
  async beforeRouteLeave(to, from, next) {
    if (this.dirty && !(await ask('Leave without saving your changes?', { action: 'Leave' }))) { next(false); return; }
    next();
  },
  methods: {
    take(data) {
      this.data = data;
      this.status = data.wall_status;
      const settings = JSON.parse(JSON.stringify(data.settings));
      settings.order = settings.order || 'newest';
      settings.selection = Object.assign({ mode: 'all', ids: [] }, settings.selection || {});
      this.settings = settings;
      this.saved = this.snapshot;
    },
    async load() {
      if (!this.brandId) return;
      this.loading = true;
      this.loadError = '';
      try { this.take(await get('/brands/' + this.brandId + '/wall')); } catch (error) { this.loadError = message(error); } finally { this.loading = false; }
    },
    pickLayout(item) {
      if (item.locked) { this.upgrade = { feature: 'layouts' }; this.upgradeOpen = true; return; }
      this.settings.layout = item.key;
    },
    toggleBranding(event) {
      if (event.target.checked && !this.data.limits.remove_branding && !this.data.settings.remove_branding) {
        event.target.checked = false;
        this.upgrade = { feature: 'remove_branding' };
        this.upgradeOpen = true;
        return;
      }
      this.settings.remove_branding = event.target.checked;
    },
    togglePick(id) {
      const ids = this.settings.selection.ids;
      const index = ids.indexOf(id);
      if (index >= 0) ids.splice(index, 1); else ids.push(id);
    },
    async save() {
      this.saving = true;
      this.error = '';
      try {
        const s = this.settings;
        this.take(await patch('/brands/' + this.brandId + '/wall', { wall_status: this.status, headline: s.headline, intro: s.intro,
          layout: s.layout, order: s.order, selection: s.selection, show_rating: s.show_rating, remove_branding: s.remove_branding }));
        toast(this.status === 'live' ? 'Saved. Your wall is live.' : 'Saved.');
        this.refreshWorkspace();
      } catch (error) {
        const limit = planLimit(error);
        if (limit) { this.upgrade = limit; this.upgradeOpen = true; } else this.error = message(error);
      } finally { this.saving = false; }
    },
    copied() { toast('Link copied.'); }
  }
};
</script>

<style scoped>
.wl-grid{display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);gap:24px;align-items:start}
.wl-settings{display:grid;gap:18px}
.wl-h{margin-bottom:14px}
.wl-link{margin-top:16px}
.wl-link-actions{margin:10px 0}
.wl-order{margin-top:16px}
.wl-gap{margin-top:10px!important}
.wl-pick{margin-top:12px;display:grid;gap:8px;max-height:360px;overflow:auto}
.wl-pick-item{padding:10px;border:1px solid #E6DED2;border-radius:10px}
.wl-pick-item>span{display:flex;flex-direction:column;min-width:0}
.wl-pick-text{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.wl-preview{position:sticky;top:84px}
.wl-frame{border:1px solid #D4C9B8;border-radius:14px;overflow:hidden;background:#FAF7F2;box-shadow:var(--pb-shadow-card)}
.wl-bar{display:flex;align-items:center;gap:6px;padding:10px 14px;background:#F3EEE6;border-bottom:1px solid #E6DED2}
.wl-bar span{width:10px;height:10px;border-radius:50%;background:#D4C9B8}
.wl-bar em{margin-left:10px;font:500 12px/1 var(--font-body);font-style:normal;color:#6B635B;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.wl-page{padding:32px;max-height:780px;overflow:auto}
@media(max-width:1100px){.wl-grid{grid-template-columns:minmax(0,1fr)}.wl-preview{position:static}}
@media(max-width:600px){.wl-page{padding:18px}}
</style>
